/**
 * Sequences Page
 * List and manage automated message sequences
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getSequencesWithAnalytics } from "../services/sequences.service";
import { getUnresolvedSequenceErrorCount } from "../services/sequenceErrors.service";
import { formatDate } from "../utils/formatters";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  BoltIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import type {
  SequenceWithAnalytics,
  SequenceStatus,
  SequenceTriggerType,
} from "../types/sequence.types";
import { TRIGGER_TYPE_OPTIONS } from "../types/sequence.types";

/**
 * "Triage N" header button. Hidden when zero unresolved errors.
 * Uses the count-only RLS-gated query so non-admin tiers don't see
 * an empty badge (RLS denial → 0 → button stays hidden).
 */
const TriageButton: React.FC = () => {
  const navigate = useNavigate();
  const { data: count = 0 } = useQuery({
    queryKey: ["sequence-step-error-count"],
    queryFn: getUnresolvedSequenceErrorCount,
    refetchInterval: 60_000,
  });

  if (count === 0) {
    return null;
  }

  return (
    <Button
      variant="secondary"
      onClick={() => navigate("/sequences/triage")}
      className="flex items-center gap-2"
    >
      <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
      Triage {count}
    </Button>
  );
};

const Sequences: React.FC = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Fetch sequences
  const {
    data: sequences,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["sequences"],
    queryFn: getSequencesWithAnalytics,
  });

  // Filter sequences
  const filteredSequences = sequences?.filter((seq) => {
    const matchesSearch =
      !searchQuery ||
      seq.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      seq.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === "all" || seq.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadgeVariant = (
    status: SequenceStatus,
  ): "default" | "success" | "error" | "warning" => {
    switch (status) {
      case "active":
        return "success";
      case "paused":
        return "warning";
      case "archived":
        return "error";
      case "draft":
      default:
        return "default";
    }
  };

  const getTriggerLabel = (triggerType: SequenceTriggerType): string => {
    const option = TRIGGER_TYPE_OPTIONS.find((t) => t.value === triggerType);
    return option?.label || triggerType;
  };

  // Summary stats
  const stats = {
    total: sequences?.length || 0,
    active: sequences?.filter((s) => s.status === "active").length || 0,
    totalEnrolled:
      sequences?.reduce((sum, s) => sum + (s.active_enrollments || 0), 0) || 0,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading sequences..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card
        variant="outlined"
        padding="md"
        className="border-red-200 bg-red-50"
      >
        <p className="text-red-800">
          Error loading sequences. Please try again.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sequences</h1>
          <p className="mt-2 text-gray-600">
            Automated message sequences for onboarding, renewals, and more
          </p>
        </div>
        <div className="flex items-center gap-3">
          <TriageButton />
          <Button
            variant="primary"
            onClick={() => navigate("/sequences/create")}
            className="flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Create Sequence
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card variant="outlined" padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BoltIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {stats.total}
              </div>
              <div className="text-sm text-gray-500">Total Sequences</div>
            </div>
          </div>
        </Card>
        <Card variant="outlined" padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <BoltIcon className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {stats.active}
              </div>
              <div className="text-sm text-gray-500">Active Sequences</div>
            </div>
          </div>
        </Card>
        <Card variant="outlined" padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {stats.totalEnrolled.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Active Enrollments</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card variant="outlined" padding="sm">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search sequences by name or description..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </Card>

      {/* Sequences Table */}
      <Card variant="outlined" padding="none">
        {filteredSequences && filteredSequences.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sequence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trigger
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Target List
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Enrollments
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSequences.map((sequence: SequenceWithAnalytics) => (
                  <tr
                    key={sequence.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/sequences/${sequence.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">
                          {sequence.name}
                        </div>
                        {sequence.description && (
                          <div className="text-sm text-gray-500 line-clamp-1">
                            {sequence.description}
                          </div>
                        )}
                        {sequence.campaign && (
                          <div className="text-xs text-blue-600 mt-1">
                            Campaign: {sequence.campaign.name}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={getStatusBadgeVariant(sequence.status)}>
                        {sequence.status.charAt(0).toUpperCase() +
                          sequence.status.slice(1)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <BoltIcon className="h-4 w-4 text-gray-400" />
                        {getTriggerLabel(sequence.trigger_type)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {sequence.list ? (
                        <div>
                          <div>{sequence.list.name}</div>
                          <div className="text-gray-500 text-xs">
                            {sequence.list.contact_count.toLocaleString()}{" "}
                            contacts
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center gap-1">
                        <UserGroupIcon className="h-4 w-4 text-gray-400" />
                        {(sequence.active_enrollments || 0).toLocaleString()}
                        <span className="text-gray-400">active</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(sequence.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/sequences/${sequence.id}`);
                        }}
                      >
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <BoltIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {searchQuery || statusFilter !== "all"
                ? "No sequences found matching your filters."
                : "No sequences yet."}
            </p>
            <p className="text-gray-400 mt-2">
              {searchQuery || statusFilter !== "all"
                ? "Try different filters."
                : "Create your first sequence to automate messaging."}
            </p>
            {!searchQuery && statusFilter === "all" && (
              <Button
                variant="primary"
                onClick={() => navigate("/sequences/create")}
                className="mt-4"
              >
                <PlusIcon className="h-5 w-5 mr-2 inline" />
                Create Sequence
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Help Section */}
      <Card
        variant="outlined"
        padding="md"
        className="bg-blue-50 border-blue-200"
      >
        <div className="flex gap-4">
          <div className="flex-shrink-0">
            <BoltIcon className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-blue-900">
              What are Message Sequences?
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              Sequences automate message delivery based on triggers. When a
              contact meets the trigger criteria (e.g., joins a list, membership
              expires), they're enrolled and receive messages on a schedule you
              define. Perfect for onboarding, renewal reminders, and drip
              campaigns.
            </p>
            <div className="flex gap-4 mt-3 text-sm text-blue-700">
              <div className="flex items-center gap-1">
                <EnvelopeIcon className="h-4 w-4" />
                Email
              </div>
              <div className="flex items-center gap-1">
                <DevicePhoneMobileIcon className="h-4 w-4" />
                SMS
              </div>
              <div className="flex items-center gap-1">
                <BoltIcon className="h-4 w-4" />
                Automated Triggers
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Sequences;
